/*
 * Counting number, ported from Animate UI (https://animate-ui.com), MIT + Commons Clause,
 * Copyright (c) 2025 Elliot Sutton. See THIRD_PARTY_NOTICES.md.
 * Change: the HTML always carries the final number (crawlers and screen readers);
 * the count up only runs in the browser once the number scrolls into view.
 */
import { useEffect, useRef } from "react";
import { useInView, useMotionValue, useSpring } from "framer-motion";
import { motionAllowed } from "./motionEnv";

export default function CountingNumber({ number, decimals = 0, suffix = "", className = "", ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 90, damping: 50 });
  const animate = motionAllowed();
  const fmt = (v) => (decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString("en-IN")) + suffix;

  useEffect(() => {
    if (!animate) return undefined;
    if (ref.current && !inView) ref.current.textContent = fmt(0);
    const off = spring.on("change", (v) => { if (ref.current) ref.current.textContent = fmt(v); });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spring, animate]);
  useEffect(() => { if (animate && inView) mv.set(number); }, [inView, number, mv, animate]);

  return <span ref={ref} className={className} aria-label={fmt(number)} {...rest}>{fmt(number)}</span>;
}
